import { SwapRightOutlined } from "@ant-design/icons"
import { getSiteUrl } from "@repo/shared"
import Iconfont from "@repo/ui/iconfont"
import classNames from "classnames"
import { useLocale, useTranslations } from "next-intl"
import Link from "next/link"
import { memo, useCallback, useMemo, useRef, useState } from "react"
import { Popover, Space } from "antd"

import ClientOnly from "../../../ClientOnly"
import { normalizeString } from "../../utils"
import { NavigationItem } from "./type"

type Point = { x: number; y: number }

// 判断点 p 是否在三角形 abc 内
// 用于检测鼠标移动轨迹是否在“安全三角”区域内 (Amazon menu-aim 算法核心逻辑)
function isPointInTriangle(p: Point, a: Point, b: Point, c: Point) {
  const sign = (p1: Point, p2: Point, p3: Point) => {
    return (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y)
  }

  const d1 = sign(p, a, b)
  const d2 = sign(p, b, c)
  const d3 = sign(p, c, a)

  const has_neg = d1 < 0 || d2 < 0 || d3 < 0
  const has_pos = d1 > 0 || d2 > 0 || d3 > 0

  return !(has_neg && has_pos)
}

/**
 * Markets 两级下拉菜单组件（使用 memo 避免父组件重渲染时不必要的整树重渲染）
 */
function MarketsDropdownInner({ item }: { item: NavigationItem }) {
  const t = useTranslations("HeaderComp")
  const locale = useLocale()
  const [hoveredIndex, setHoveredIndex] = useState<number>(0)
  const mainItems = useMemo(() => item.children || [], [item.children])

  // 引用和状态用于防误触逻辑
  const mouseLocsRef = useRef<Point[]>([])
  const submenuRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 记录鼠标轨迹（稳定引用，避免子组件因 onMouseMove 引用变化重渲染）
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    mouseLocsRef.current.push({ x: e.clientX, y: e.clientY })
    if (mouseLocsRef.current.length > 3) {
      mouseLocsRef.current.shift()
    }
  }, [])

  // 鼠标离开整个菜单区域
  const handleMouseLeave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setHoveredIndex(0)
    mouseLocsRef.current = []
  }, [])

  // 鼠标进入左侧菜单项（依赖 hoveredIndex 用于安全三角判断）
  const handleMouseEnterItem = useCallback(
    (index: number) => {
      if (timerRef.current) clearTimeout(timerRef.current)

      if (hoveredIndex === null) {
        setHoveredIndex(index)
        return
      }

      const prevLoc = mouseLocsRef.current[0]
      const currLoc = mouseLocsRef.current[mouseLocsRef.current.length - 1]
      const submenu = submenuRef.current

      if (!prevLoc || !currLoc || !submenu) {
        setHoveredIndex(index)
        return
      }

      const rect = submenu.getBoundingClientRect()
      const topLeft = { x: rect.left, y: rect.top }
      const bottomLeft = { x: rect.left, y: rect.bottom }

      // 如果鼠标移动轨迹在当前鼠标位置与子菜单上下角组成的三角形内
      // 说明用户可能想移动到子菜单，因此延迟切换
      if (isPointInTriangle(currLoc, prevLoc, topLeft, bottomLeft)) {
        timerRef.current = setTimeout(() => {
          setHoveredIndex(index)
        }, 300) // 300ms 延迟
      } else {
        setHoveredIndex(index)
      }
    },
    [hoveredIndex]
  )

  // 进入右侧子菜单时清除切换定时器，稳定引用
  const handleSubmenuMouseEnter = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
  }, [])

  const dropdownContent = useMemo(
    () => (
      <div className="marketsDropdown" onMouseMove={handleMouseMove}>
        <div className="dropdownLeft">
          <div className="watchlistButtonContainer">
            <Link href={getSiteUrl("www") + "/" + locale + "/watchlist"} prefetch={false} className="watchlistButton">
              <Space size={4}>
                {t("utilityNav.MyWatchlist")}
                <SwapRightOutlined className="watchlistIcon" style={{ fontSize: 14 }} />
              </Space>
            </Link>
          </div>

          {/* 主菜单项列表 */}
          <div className="dropdownItems">
            {mainItems.map((mainItem: NavigationItem, index: number) => (
              <div
                key={
                  mainItem.href && mainItem.href !== "#"
                    ? mainItem.href
                    : typeof mainItem.title === "string"
                      ? mainItem.title
                      : String(index)
                }
                className={classNames("dropdownItem", { hovered: hoveredIndex === index })}
                onMouseEnter={() => handleMouseEnterItem(index)}
              >
                <Link
                  href={mainItem.href || "#"}
                  className="dropdownLink"
                  prefetch={false}
                  {...(mainItem.openInNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                >
                  <span>{mainItem.title}</span>
                  {mainItem.isHot && (
                    <Iconfont
                      type="icon-huo"
                      className="hotIcon"
                      style={{
                        color: "#D7000F",
                        fontSize: 15,
                        marginLeft: 4,
                      }}
                    />
                  )}
                </Link>
                {mainItem.children && mainItem.children.length > 0 && (
                  <Iconfont
                    type="icon-xiayijijiantou"
                    width={20}
                    height={20}
                    style={{
                      color: "#0C1019",
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 右侧二级菜单 */}
        {mainItems[hoveredIndex]?.children && mainItems[hoveredIndex].children!.length > 0 && (
          <div className="dropdownRight" ref={submenuRef} onMouseEnter={handleSubmenuMouseEnter}>
            <div className="subItems">
              {mainItems[hoveredIndex].children!.map((subItem: NavigationItem, subIndex: number) => {
                const title = normalizeString(subItem.href?.toString() || "")
                const key =
                  subItem.href && subItem.href !== "#"
                    ? subItem.href
                    : typeof subItem.title === "string"
                      ? `${subItem.title}-${subIndex}`
                      : `subItem-${subIndex}`
                return (
                  <Link
                    key={key}
                    href={getSiteUrl("www") + "/" + locale + "/" + `${title}`}
                    className={`subItem`}
                    prefetch={false}
                    {...(subItem.openInNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  >
                    {subItem.title}
                  </Link>
                )
              })}
            </div>
          </div>
        )}
      </div>
    ),
    [mainItems, hoveredIndex, locale, t, handleMouseMove, handleMouseEnterItem, handleSubmenuMouseEnter]
  )

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) handleMouseLeave()
    },
    [handleMouseLeave]
  )

  const fallbackContent = useMemo(
    () => (
      <div className="navItem">
        <span className="navItemText">{item.title}</span>
        <Iconfont width={20} height={20} type="icon-xialacu" style={{ fontSize: 20 }} />
      </div>
    ),
    [item.title]
  )

  const triggerContent = useMemo(
    () => (
      <div className="navItem">
        <span className="navItemText">{item.title}</span>
        <Iconfont width={20} height={20} type="icon-xialacu" style={{ fontSize: 20 }} />
      </div>
    ),
    [item.title]
  )

  return (
    <ClientOnly fallback={fallbackContent}>
      <Popover
        content={dropdownContent}
        trigger="hover"
        placement="bottomLeft"
        classNames={{
          root: "marketsPopover marketsPopoverGlobal",
        }}
        getPopupContainer={(triggerNode) =>
          triggerNode?.closest?.(".headerWrapper") ?? document.querySelector(".headerWrapper") ?? document.body
        }
        onOpenChange={handleOpenChange}
        destroyOnHidden
      >
        {triggerContent}
      </Popover>
    </ClientOnly>
  )
}

export const MarketsDropdown = memo(MarketsDropdownInner)
